import React from 'react';
import {
  getTextFieldUtilityClass,
  styled,
  unstable_composeClasses as composeClasses,
  useThemeProps,
  FormHelperText,
  OutlinedInput,
  Select,
  FormControlTypeMap,
} from '@mui/material';
import clsx from 'clsx';
import { OverridableComponent } from '@mui/types';
import { InputLabel } from '../inputLabel';
import { TextFieldProps } from './types';
import useFormFieldId from '../utilities/useFormFieldId';
import { FormControl } from '../form';

const useUtilityClasses = (ownerState: TextFieldProps) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
  };

  return composeClasses(slots, getTextFieldUtilityClass, classes);
};

type TextFieldRootComponent = OverridableComponent<FormControlTypeMap<{ ownerState: unknown, labelPosition: undefined | 'left' }, 'div'>>;

const TextFieldRoot = styled<TextFieldRootComponent>(FormControl as TextFieldRootComponent, {
  name: 'MuiTextField',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})({});

export const TextField = React.forwardRef(
  (inProps: TextFieldProps, ref: React.RefObject<HTMLDivElement>) => {
    const props = useThemeProps({ props: inProps, name: 'MuiTextField' });
    const {
      autoComplete,
      autoFocus = false,
      children,
      className,
      defaultValue,
      disabled = false,
      error = false,
      FormHelperTextProps,
      fullWidth = false,
      helperText,
      id: idOverride,
      inputProps,
      InputProps,
      inputRef,
      label,
      maxRows,
      minRows,
      multiline = false,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      required = false,
      rows,
      select = false,
      SelectProps,
      type,
      value,
      labelPosition,
      ...other
    } = props;

    const color = 'primary';
    const variant = 'outlined';

    const ownerState = {
      ...props,
      autoFocus,
      disabled,
      error,
      fullWidth,
      multiline,
      required,
      select,
      variant,
    };

    const classes = useUtilityClasses(ownerState);

    const [id, helperTextId, inputLabelId] = useFormFieldId({
      idOverride, helperText, label,
    });

    const InputElement = (
      <OutlinedInput
        aria-describedby={helperTextId}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        multiline={multiline}
        name={name}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        type={type}
        value={value}
        id={id}
        inputRef={inputRef}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        inputProps={inputProps}
        {...InputProps}
      />
    );

    return (
      <TextFieldRoot
        labelPosition={labelPosition}
        className={clsx(classes.root, className)}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        ref={ref}
        required={required}
        color={color}
        variant={variant}
        ownerState={ownerState}
        {...other}
      >
        {label != null && label !== '' && (
          <InputLabel
            htmlFor={id}
            id={inputLabelId}
            required={required}
            size={props.size}
            {...inProps.InputLabelProps}
          >
            {label}
          </InputLabel>
        )}

        {select ? (
          <Select
            aria-describedby={helperTextId}
            id={id}
            labelId={inputLabelId}
            value={value}
            input={InputElement}
            {...SelectProps}
          >
            {children}
          </Select>
        ) : (
          InputElement
        )}

        {helperText && (
          <FormHelperText id={helperTextId} {...FormHelperTextProps}>
            {helperText}
          </FormHelperText>
        )}
      </TextFieldRoot>
    );
  },
);
